import React from 'react'
import {App as AntdApp, Checkbox, List, Modal, Tag, Typography} from 'antd'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_TRAFFIC_DRIVERS, UPDATE_TRAFFIC_DRIVERS} from '../../../utils/api/trafficDriver'
import {TeaserStatus, Teaser} from '../../../__generated__/graphql'
import {trackEvent} from '../../../utils/analytics'
import {getTrafficDriverImageUrl} from '../../../utils/cloudinary'
import {withAsyncErrorToast} from '../../../utils/withToast'

type PromptArgs = {
  campaignId: string
  currentWebsiteIds: string[]
  newWebsiteIds: string[]
}

type TeaserCandidate = Teaser & {websiteIds: string[]}

export const useTeaserExtensionPrompt = () => {
  const {notification} = AntdApp.useApp()
  const [visible, setVisible] = React.useState(false)
  const [processing, setProcessing] = React.useState(false)
  const [teasers, setTeasers] = React.useState<TeaserCandidate[]>([])
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  const [newWebsiteIds, setNewWebsiteIds] = React.useState<string[]>([])
  const resolverRef = React.useRef<(() => void) | null>(null)

  const [loadTeasers, {loading: loadingTeasers}] = useLazyQuery(GET_TRAFFIC_DRIVERS, {
    fetchPolicy: 'network-only',
  })
  const [updateTrafficDrivers] = useMutation(UPDATE_TRAFFIC_DRIVERS, {
    refetchQueries: ['TeaserMany'],
    awaitRefetchQueries: true,
  })

  const close = () => {
    setVisible(false)
    setTeasers([])
    setSelectedIds([])
    setNewWebsiteIds([])
    resolverRef.current?.()
    resolverRef.current = null
  }

  const prompt = async ({campaignId, currentWebsiteIds, newWebsiteIds}: PromptArgs) => {
    if (!newWebsiteIds.length) {
      return
    }

    const {data} = await loadTeasers({
      variables: {
        filters: {campaignId, status: [TeaserStatus.Active, TeaserStatus.Inactive]},
        perPage: 1000,
      },
    })

    const teaserItems: TeaserCandidate[] = data?.trafficDrivers?.items?.map((item: any) => ({
      ...item,
      websiteIds: item.websiteIds || [],
    })) || []

    const candidates = teaserItems.filter((teaser) => {
      if (teaser.status === TeaserStatus.Deleted) {
        return false
      }
      const websiteIds = teaser.websiteIds || []
      if (!websiteIds.length) {
        return false
      }
      return currentWebsiteIds.every((id) => websiteIds.includes(id))
    })

    if (!candidates.length) {
      return
    }

    setTeasers(candidates)
    setSelectedIds(candidates.map(({_id}) => _id))
    setNewWebsiteIds(newWebsiteIds)
    setVisible(true)
    trackEvent('teaser_extension_prompt_shown', {
      campaignId,
      teaserCount: candidates.length,
      newWebsiteCount: newWebsiteIds.length,
    })

    return new Promise<void>((resolve) => {
      resolverRef.current = resolve
    })
  }

  const handleConfirm = async () => {
    const selectedTeasers = teasers.filter(({_id}) => selectedIds.includes(_id))

    if (!selectedTeasers.length) {
      trackEvent('teaser_extension_skipped', {
        reason: 'no_teasers_selected',
        newWebsiteCount: newWebsiteIds.length,
      })
      return close()
    }

    setProcessing(true)
    try {
      const records = selectedTeasers.map((teaser) => ({
        id: teaser._id,
        record: {
          websiteIds: Array.from(new Set([...(teaser.websiteIds || []), ...newWebsiteIds])),
        },
      }))

      await withAsyncErrorToast('Update teasers', async () => {
        await updateTrafficDrivers({variables: {records}})
      },
        undefined,
        notification
      )

      trackEvent('teaser_extension_confirmed', {
        teaserCount: selectedTeasers.length,
        newWebsiteCount: newWebsiteIds.length,
      })
      close()
    } catch (error) {
      // keep modal open so the user can retry after a failure
    } finally {
      setProcessing(false)
    }
  }

  const handleCancel = () => {
    trackEvent('teaser_extension_skipped', {
      reason: 'user_cancelled',
      newWebsiteCount: newWebsiteIds.length,
    })
    close()
  }

  const modal = (
    <Modal
      open={visible}
      title='Apply existing teasers to new websites?'
      onOk={handleConfirm}
      onCancel={handleCancel}
      okText='Add to new websites'
      cancelText='Skip'
      confirmLoading={processing}
      destroyOnClose
    >
      <Typography.Paragraph>
        Select which teasers to copy to the new websites.
      </Typography.Paragraph>
      <List
        loading={loadingTeasers}
        dataSource={teasers}
        renderItem={(item) => (
          <List.Item className='flex items-center'>
            <Checkbox
              checked={selectedIds.includes(item._id)}
              onChange={(e) => {
                const checked = e.target.checked
                setSelectedIds((prev) =>
                  checked ? [...prev, item._id] : prev.filter((id) => id !== item._id)
                )
              }}
              className='flex-1'
            >
              <div className='flex items-center'>
                <span>{item.headline || 'Untitled teaser'}</span>
                <Tag className='ml-2' color={item.status === TeaserStatus.Active ? 'green' : 'default'}>
                  {item.status?.toLowerCase()}
                </Tag>
              </div>
            </Checkbox>
            {item.imageId && (
              <img
                src={getTrafficDriverImageUrl(item.imageId)}
                alt={item.headline || 'Teaser'}
                className='h-12 w-16 object-cover rounded ml-3'
              />
            )}
          </List.Item>
        )}
      />
    </Modal>
  )

  return {promptTeaserExtension: prompt, teaserExtensionModal: modal}
}
